import { Ref, useCallback, useEffect, useState } from 'react';
import { isThenable } from 'ramda-adjunct';
import { useBoolean } from '../../../../../../utils/hooks/useBoolean';
import AlphaVideo2DPlayer from './VideoAlpha2DPlayer';

type TVideoOnHoverControls = {
  setVideoElement: Ref<HTMLVideoElement>;
  setCanvasElement: Ref<HTMLCanvasElement>;
  isHovered: boolean;
  isPlaying: boolean;
  setHoveredTrue: () => void;
  handleVideoNodeMouseLeave: () => void;
};

export const useVideoOnHoverControls = (): TVideoOnHoverControls => {
  const [videoElement, setVideoElement] = useState(null);
  const [canvasElement, setCanvasElement] = useState(null);
  const { value: isPlaying, setTrue: setPlayingTrue, setFalse: setPlayingFalse } = useBoolean();
  const { value: isHovered, setTrue: setHoveredTrue, setFalse: setHoveredFalse } = useBoolean();

  const handleVideoNodeMouseLeave = () => {
    setHoveredFalse();
    setPlayingFalse();
  };

  const startPlayingVideoThumbnail = useCallback(() => {
    const playPromise = videoElement.play();

    if (isThenable(playPromise)) {
      playPromise.catch(setHoveredFalse);
    }
  }, [videoElement]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    let player;

    const handlePlaying = () => {
      if (canvasElement) {
        if (!player) {
          player = new AlphaVideo2DPlayer(canvasElement);
        }

        player.play(videoElement);

        setPlayingTrue();
      }
    };

    videoElement.addEventListener('playing', handlePlaying);

    startPlayingVideoThumbnail();

    return () => {
      if (player) {
        player.stop();
        player = null;
      }
      videoElement.removeEventListener('playing', handlePlaying);
    };
  }, [videoElement, canvasElement]);

  return {
    setVideoElement,
    setCanvasElement,
    isHovered,
    isPlaying,
    setHoveredTrue,
    handleVideoNodeMouseLeave,
  };
};
